import { useEffect, useState, useCallback } from 'react';
import { challengesAPI, speakersAPI } from '../../lib/api';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ImageUpload } from '../../components/ui/image-upload';
import { BlocksTab } from '../../components/admin/events/BlocksTab';
import { SpawnChildDialog } from '../../components/admin/challenges/SpawnChildDialog';
import { JotformWebhookPanel } from '../../components/admin/challenges/JotformWebhookPanel';
import { ImportStartupsDialog } from '../../components/admin/challenges/ImportStartupsDialog';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Trophy, Users, Rocket, BarChart3,
  Send, Loader2, Search, Clock, Mail, CheckCircle, XCircle, Lock, Layers, Save, Eye, EyeOff
} from 'lucide-react';

export const ChallengesAdminPage = () => {
  const [challenges, setChallenges] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Challenge form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', event_id: '', start_time: '', end_time: '', description: '', status: 'draft', nav_label: '', content_sections: [] });

  // Detail view
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [judges, setJudges] = useState([]);
  const [startups, setStartups] = useState([]);
  const [results, setResults] = useState(null);

  // Judge form
  const [judgeDialogOpen, setJudgeDialogOpen] = useState(false);
  const [editingJudge, setEditingJudge] = useState(null);
  const [judgeForm, setJudgeForm] = useState({ first_name: '', last_name: '', job_title: '', bio: '', email: '', image: '' });
  const [speakerSearch, setSpeakerSearch] = useState('');
  const [speakerResults, setSpeakerResults] = useState([]);

  // Startup form
  const [startupDialogOpen, setStartupDialogOpen] = useState(false);
  const [editingStartup, setEditingStartup] = useState(null);
  const [startupForm, setStartupForm] = useState({ name: '', logo: '', description: '', website: '', linkedin: '', instagram: '', founder_photo: '' });
  const [startupSearch, setStartupSearch] = useState('');
  const [startupSearchResults, setStartupSearchResults] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Email
  const [sending, setSending] = useState(false);
  const [selectedJudgeIds, setSelectedJudgeIds] = useState([]);

  // Spawn child
  const [spawnDialogOpen, setSpawnDialogOpen] = useState(false);
  const [spawnTitle, setSpawnTitle] = useState('');
  const [spawnStartupIds, setSpawnStartupIds] = useState([]);
  const [spawning, setSpawning] = useState(false);

  // Content (blocks)
  const [contentForm, setContentForm] = useState({ content_sections: [] });
  const [savingContent, setSavingContent] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [chRes, evRes] = await Promise.all([
        challengesAPI.getAll(),
        api.get('/events', { params: { include_drafts: true } }),
      ]);
      setChallenges(chRes.data);
      setEvents(evRes.data);
    } catch (err) { console.error(err); toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  const loadChallengeDetail = useCallback(async (challenge) => {
    setSelectedChallenge(challenge);
    setContentForm({ content_sections: challenge.content_sections || [] });
    try {
      const [jRes, sRes, rRes] = await Promise.all([
        challengesAPI.getJudges(challenge.id),
        challengesAPI.getStartups(challenge.id),
        challengesAPI.getResults(challenge.id),
      ]);
      setJudges(jRes.data);
      setStartups(sRes.data);
      setResults(rRes.data);
    } catch (err) { console.error(err); }
  }, []);

  const saveChallengeContent = async () => {
    if (!selectedChallenge) return;
    setSavingContent(true);
    try {
      const res = await challengesAPI.update(selectedChallenge.id, { content_sections: contentForm.content_sections });
      setSelectedChallenge(res.data);
      // Refresh in list
      setChallenges(prev => prev.map(c => c.id === res.data.id ? res.data : c));
      toast.success('Contenuto pagina salvato');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore salvataggio'); }
    finally { setSavingContent(false); }
  };

  // ─── Challenge CRUD ───
  const resetForm = () => { setForm({ title: '', event_id: '', start_time: '', end_time: '', description: '', status: 'draft', nav_label: '', content_sections: [] }); setEditing(null); };

  const openEdit = (ch) => {
    setEditing(ch);
    setForm({
      title: ch.title,
      event_id: ch.event_id || '',
      start_time: ch.start_time || '',
      end_time: ch.end_time || '',
      description: ch.description || '',
      status: ch.status || 'draft',
      nav_label: ch.nav_label || '',
      content_sections: ch.content_sections || [],
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await challengesAPI.update(editing.id, form); toast.success('Challenge aggiornata'); }
      else { await challengesAPI.create(form); toast.success('Challenge creata'); }
      setDialogOpen(false); resetForm(); loadData();
    } catch (err) { toast.error('Errore nel salvataggio'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Eliminare questa challenge e tutti i dati collegati?')) return;
    await challengesAPI.delete(id);
    toast.success('Challenge eliminata');
    if (selectedChallenge?.id === id) setSelectedChallenge(null);
    loadData();
  };

  // ─── Judges ───
  const resetJudgeForm = () => { setJudgeForm({ first_name: '', last_name: '', job_title: '', bio: '', email: '', image: '' }); setEditingJudge(null); setSpeakerSearch(''); setSpeakerResults([]); };

  const searchSpeakers = async (q) => {
    setSpeakerSearch(q);
    if (q.length < 2) { setSpeakerResults([]); return; }
    try {
      const res = await challengesAPI.searchJudges(q, selectedChallenge?.id);
      setSpeakerResults((res.data || []).slice(0, 8));
    } catch (err) { console.error(err); setSpeakerResults([]); }
  };

  const fillFromSpeaker = (s) => {
    setJudgeForm({
      first_name: s.first_name || '',
      last_name: s.last_name || '',
      job_title: s.job_title || '',
      bio: s.bio || '',
      email: s.email || '',
      image: s.image || '',
    });
    setSpeakerSearch('');
    setSpeakerResults([]);
  };

  const handleJudgeSubmit = async (e) => {
    e.preventDefault();
    if (!selectedChallenge) return;
    try {
      if (editingJudge) {
        await challengesAPI.updateJudge(editingJudge.id, judgeForm);
        toast.success('Giurato aggiornato');
      } else {
        await challengesAPI.addJudge(selectedChallenge.id, { ...judgeForm, challenge_id: selectedChallenge.id });
        toast.success('Giurato aggiunto');
      }
      setJudgeDialogOpen(false); resetJudgeForm();
      loadChallengeDetail(selectedChallenge);
    } catch (err) { toast.error('Errore nel salvataggio giurato'); }
  };

  const deleteJudge = async (id) => {
    if (!confirm('Rimuovere questo giurato?')) return;
    await challengesAPI.deleteJudge(id);
    toast.success('Giurato rimosso');
    loadChallengeDetail(selectedChallenge);
  };

  const toggleJudgeVisibility = async (judge) => {
    const next = judge.is_visible === false;
    try {
      await challengesAPI.updateJudge(judge.id, { is_visible: next });
      toast.success(next ? 'Giurato visibile sul sito' : 'Giurato nascosto dal sito');
      loadChallengeDetail(selectedChallenge);
    } catch (err) {
      toast.error('Errore aggiornamento visibilità');
    }
  };

  // ─── Startups ───
  const resetStartupForm = () => { setStartupForm({ name: '', logo: '', description: '', website: '', linkedin: '', instagram: '', founder_photo: '' }); setEditingStartup(null); setStartupSearch(''); setStartupSearchResults([]); };

  const searchStartups = async (q) => {
    setStartupSearch(q);
    if (q.length < 2) { setStartupSearchResults([]); return; }
    try {
      const res = await challengesAPI.searchStartups(q, selectedChallenge?.id);
      setStartupSearchResults((res.data || []).slice(0, 8));
    } catch (err) { console.error(err); setStartupSearchResults([]); }
  };

  const fillFromStartup = (s) => {
    setStartupForm({
      name: s.name || '',
      logo: s.logo || '',
      description: s.description || '',
      website: s.website || '',
      linkedin: s.linkedin || '',
      instagram: s.instagram || '',
      founder_photo: s.founder_photo || '',
    });
    setStartupSearch('');
    setStartupSearchResults([]);
  };

  const handleStartupSubmit = async (e) => {
    e.preventDefault();
    if (!selectedChallenge) return;
    try {
      if (editingStartup) {
        await challengesAPI.updateStartup(editingStartup.id, startupForm);
        toast.success('Startup aggiornata');
      } else {
        await challengesAPI.addStartup(selectedChallenge.id, { ...startupForm, challenge_id: selectedChallenge.id });
        toast.success('Startup aggiunta');
      }
      setStartupDialogOpen(false); resetStartupForm();
      loadChallengeDetail(selectedChallenge);
    } catch (err) { toast.error('Errore nel salvataggio startup'); }
  };

  const deleteStartup = async (id) => {
    if (!confirm('Rimuovere questa startup?')) return;
    await challengesAPI.deleteStartup(id);
    toast.success('Startup rimossa');
    loadChallengeDetail(selectedChallenge);
  };

  // ─── Email ───
  const sendEmails = async (judgeIds) => {
    setSending(true);
    try {
      const res = await challengesAPI.sendEmails(selectedChallenge.id, { judge_ids: judgeIds || null, base_url: window.location.origin });
      toast.success(`Email inviate: ${res.data.sent}/${res.data.total}`);
      if (res.data.errors?.length) toast.error(`Errori: ${res.data.errors.length}`);
      // refresh results so the email_sent_count badge updates
      loadChallengeDetail(selectedChallenge);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio email'); }
    finally { setSending(false); }
  };

  const deleteJudgeVote = async (judgeId) => {
    if (!confirm('Cancellare il voto di questo giurato? Potrà votare di nuovo.')) return;
    try {
      await challengesAPI.deleteVote(selectedChallenge.id, judgeId);
      toast.success('Voto cancellato');
      loadChallengeDetail(selectedChallenge);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore cancellazione voto'); }
  };

  // ─── Spawn child challenge ───
  const openSpawnDialog = () => {
    setSpawnTitle(`${selectedChallenge.title} — Round successivo`);
    const top = [...(results?.ranking || [])]
      .sort((a, b) => (b.votes || 0) - (a.votes || 0))
      .slice(0, 3)
      .map(r => r.startup_id);
    setSpawnStartupIds(top);
    setSpawnDialogOpen(true);
  };

  const toggleSpawnStartup = (sid) => {
    setSpawnStartupIds(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);
  };

  const handleSpawn = async () => {
    if (!spawnTitle.trim() || spawnStartupIds.length === 0) {
      toast.error('Inserisci un titolo e seleziona almeno una startup');
      return;
    }
    setSpawning(true);
    try {
      const res = await challengesAPI.spawn(selectedChallenge.id, {
        title: spawnTitle.trim(),
        startup_ids: spawnStartupIds,
      });
      toast.success('Nuova challenge creata in bozza');
      setSpawnDialogOpen(false);
      await loadData();
      loadChallengeDetail(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore creazione round successivo');
    } finally { setSpawning(false); }
  };

  const getEventTitle = (eid) => events.find(e => e.id === eid)?.title || '-';

  // ═══════════ RENDER ═══════════

  if (selectedChallenge) {
    return (
      <div className="space-y-6" data-testid="challenge-detail">
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => setSelectedChallenge(null)} className="text-sm text-muted-foreground hover:text-white mb-2 flex items-center gap-1">&larr; Torna alla lista</button>
            <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><Trophy className="text-primary" size={24} />{selectedChallenge.title}</h1>
            <p className="text-sm text-muted-foreground mt-1">{selectedChallenge.start_time?.slice(0,16).replace('T',' ')} — {selectedChallenge.end_time?.slice(0,16).replace('T',' ')}</p>
            {selectedChallenge.parent_challenge_id && (
              <button
                onClick={() => {
                  const parent = challenges.find(c => c.id === selectedChallenge.parent_challenge_id);
                  if (parent) loadChallengeDetail(parent);
                }}
                className="mt-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-primary/15 text-primary hover:bg-primary/25 transition"
                data-testid="parent-challenge-link"
              >
                <Trophy size={11} /> Round successivo di: {challenges.find(c => c.id === selectedChallenge.parent_challenge_id)?.title || 'challenge precedente'}
              </button>
            )}
          </div>
          <Button onClick={() => openEdit(selectedChallenge)} variant="outline" size="sm"><Pencil size={14} className="mr-1" />Modifica</Button>
        </div>

        <Tabs defaultValue="judges">
          <TabsList>
            <TabsTrigger value="judges"><Users size={14} className="mr-1" />Giurati ({judges.length})</TabsTrigger>
            <TabsTrigger value="startups"><Rocket size={14} className="mr-1" />Startup ({startups.length})</TabsTrigger>
            <TabsTrigger value="results"><BarChart3 size={14} className="mr-1" />Risultati</TabsTrigger>
            <TabsTrigger value="content" data-testid="challenge-content-tab"><Layers size={14} className="mr-1" />Contenuto Pagina</TabsTrigger>
          </TabsList>

          {/* ─── JUDGES TAB ─── */}
          <TabsContent value="judges" className="space-y-4 mt-4">
            <JotformWebhookPanel challengeId={selectedChallenge.id} />
            {selectedChallenge.status !== 'draft' && (
              <div className="glass rounded-lg p-4 space-y-2 border border-primary/20">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-sm font-medium flex items-center gap-2">
                      <Send size={14} className="text-primary" /> Link votazione pubblico
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Condividi questo link con tutti i giurati: basta che inseriscano la loro email per accedere alla votazione.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const url = `${window.location.origin}/challenges/${selectedChallenge.slug}/vote`;
                      navigator.clipboard.writeText(url);
                      toast.success('Link copiato negli appunti');
                    }}
                    data-testid="copy-vote-link-btn"
                  >
                    Copia link
                  </Button>
                </div>
                <code
                  className="block text-xs font-mono bg-white/5 rounded px-3 py-2 border border-white/10 break-all"
                  data-testid="vote-link-display"
                >
                  {`${window.location.origin}/challenges/${selectedChallenge.slug}/vote`}
                </code>
              </div>
            )}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-muted-foreground">{judges.length} giurati</p>
              <div className="flex gap-2 flex-wrap">
                {selectedChallenge.status === 'draft' && (
                  <span className="text-xs text-yellow-400 bg-yellow-500/10 px-3 py-1.5 rounded-full flex items-center gap-1">
                    <Lock size={12} /> Pubblica la challenge per inviare email
                  </span>
                )}
                {selectedChallenge.status !== 'draft' && selectedJudgeIds.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => sendEmails(selectedJudgeIds)} disabled={sending}>
                    {sending ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
                    Invia ai Selezionati ({selectedJudgeIds.length})
                  </Button>
                )}
                {selectedChallenge.status !== 'draft' && (
                  <Button size="sm" variant="outline" onClick={() => sendEmails(null)} disabled={sending || judges.length === 0}>
                    {sending ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
                    Invia a Tutti
                  </Button>
                )}
                <Button size="sm" onClick={() => { resetJudgeForm(); setJudgeDialogOpen(true); }} data-testid="add-judge-btn">
                  <Plus size={14} className="mr-1" />Aggiungi Giurato
                </Button>
              </div>
            </div>
            {judges.length > 1 && selectedChallenge.status !== 'draft' && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedJudgeIds.length === judges.length}
                  onChange={(e) => setSelectedJudgeIds(e.target.checked ? judges.map(j => j.id) : [])}
                  className="rounded border-white/20"
                />
                Seleziona tutti
              </label>
            )}
            <div className="space-y-2">
              {judges.map(j => (
                <div key={j.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10" data-testid={`judge-${j.id}`}>
                  <div className="flex items-center gap-3">
                    {selectedChallenge.status !== 'draft' && (
                      <input
                        type="checkbox"
                        checked={selectedJudgeIds.includes(j.id)}
                        onChange={(e) => setSelectedJudgeIds(prev => e.target.checked ? [...prev, j.id] : prev.filter(id => id !== j.id))}
                        className="rounded border-white/20"
                      />
                    )}
                    {j.image ? <img src={j.image.startsWith('http') ? j.image : `${process.env.REACT_APP_BACKEND_URL}${j.image}`} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center"><Users size={16} className="text-primary" /></div>}
                    <div>
                      <p className="font-medium">{j.first_name} {j.last_name}</p>
                      <p className="text-xs text-muted-foreground">{j.job_title || ''} {j.email ? `· ${j.email}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground font-mono">{j.access_code}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleJudgeVisibility(j)}
                      title={j.is_visible === false ? 'Nascosto: click per mostrare sul sito' : 'Visibile sul sito: click per nascondere'}
                      data-testid={`toggle-judge-visibility-${j.id}`}
                      className={j.is_visible === false ? 'text-muted-foreground' : 'text-primary'}
                    >
                      {j.is_visible === false ? <EyeOff size={14} /> : <Eye size={14} />}
                    </Button>
                    {selectedChallenge.status !== 'draft' && (
                      <Button variant="ghost" size="sm" onClick={() => sendEmails([j.id])} disabled={sending} title="Invia email"><Send size={14} /></Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => { setEditingJudge(j); setJudgeForm({ first_name: j.first_name, last_name: j.last_name, job_title: j.job_title || '', bio: j.bio || '', email: j.email, image: j.image || '' }); setJudgeDialogOpen(true); }}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => deleteJudge(j.id)}><Trash2 size={14} /></Button>
                  </div>
                </div>
              ))}
              {judges.length === 0 && <p className="text-center py-8 text-muted-foreground">Nessun giurato aggiunto</p>}
            </div>
          </TabsContent>

          {/* ─── STARTUPS TAB ─── */}
          <TabsContent value="startups" className="space-y-4 mt-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-muted-foreground">{startups.length} startup</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setImportDialogOpen(true)} data-testid="import-startups-btn">
                  <Plus size={14} className="mr-1" />Importa Excel
                </Button>
                <Button size="sm" onClick={() => { resetStartupForm(); setStartupDialogOpen(true); }} data-testid="add-startup-btn">
                  <Plus size={14} className="mr-1" />Aggiungi Startup
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {startups.map(s => (
                <div key={s.id} className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10" data-testid={`startup-${s.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {s.logo ? <img src={s.logo.startsWith('http') ? s.logo : `${process.env.REACT_APP_BACKEND_URL}${s.logo}`} alt="" className="w-12 h-12 rounded-lg object-contain bg-white/10 p-1" /> : <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center"><Rocket size={20} className="text-primary" /></div>}
                    <div className="min-w-0">
                      <p className="font-medium truncate">{s.name}</p>
                      {s.description && <p className="text-xs text-muted-foreground line-clamp-1">{s.description}</p>}
                    </div>
                  </div>
                  <div className="flex gap-1 ml-2">
                    <Button variant="ghost" size="sm" onClick={() => { setEditingStartup(s); setStartupForm({ name: s.name, logo: s.logo || '', description: s.description || '', website: s.website || '', linkedin: s.linkedin || '', instagram: s.instagram || '', founder_photo: s.founder_photo || '' }); setStartupDialogOpen(true); }}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => deleteStartup(s.id)}><Trash2 size={14} /></Button>
                  </div>
                </div>
              ))}
              {startups.length === 0 && <p className="text-center py-8 text-muted-foreground col-span-2">Nessuna startup aggiunta</p>}
            </div>
          </TabsContent>

          {/* ─── RESULTS TAB ─── */}
          <TabsContent value="results" className="space-y-6 mt-4">
            {results && (
              <>
                <div className="grid grid-cols-4 gap-4">
                  <div className="glass rounded-lg p-4 text-center"><p className="text-2xl font-bold">{results.total_judges}</p><p className="text-xs text-muted-foreground">Giurati</p></div>
                  <div className="glass rounded-lg p-4 text-center"><p className="text-2xl font-bold">{results.email_sent_count || 0}</p><p className="text-xs text-muted-foreground">Email Inviate</p></div>
                  <div className="glass rounded-lg p-4 text-center"><p className="text-2xl font-bold">{results.voted_judges}</p><p className="text-xs text-muted-foreground">Hanno Votato</p></div>
                  <div className="glass rounded-lg p-4 text-center"><p className="text-2xl font-bold">{results.total_votes}</p><p className="text-xs text-muted-foreground">Voti Totali</p></div>
                </div>

                {/* Ranking */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h3 className="font-medium flex items-center gap-2"><BarChart3 size={16} className="text-primary" />Classifica</h3>
                    {results.ranking?.length > 0 && (
                      <Button size="sm" variant="outline" onClick={openSpawnDialog} data-testid="spawn-child-btn">
                        <Trophy size={14} className="mr-1" />Crea Round Successivo
                      </Button>
                    )}
                  </div>
                  {results.ranking?.map(r => (
                    <div key={r.startup_id} className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex items-center gap-4">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${r.position === 1 ? 'bg-yellow-500/20 text-yellow-400' : r.position === 2 ? 'bg-gray-400/20 text-gray-300' : r.position === 3 ? 'bg-orange-500/20 text-orange-400' : 'bg-white/10 text-muted-foreground'}`}>{r.position}</span>
                        {r.startup_logo ? <img src={r.startup_logo.startsWith('http') ? r.startup_logo : `${process.env.REACT_APP_BACKEND_URL}${r.startup_logo}`} alt="" className="w-10 h-10 rounded-lg object-contain bg-white/10 p-1" /> : null}
                        <span className="font-medium">{r.startup_name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xl font-bold text-primary">{r.votes}</span>
                        <span className="text-xs text-muted-foreground">voti</span>
                      </div>
                    </div>
                  ))}
                  {(!results.ranking || results.ranking.length === 0) && <p className="text-center py-4 text-muted-foreground">Nessun voto registrato</p>}
                </div>

                {/* Judges Report */}
                {results.judges_report?.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-medium flex items-center gap-2"><Users size={16} className="text-primary" />Report Giurati</h3>
                    <div className="glass rounded-xl overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-white/5">
                            <TableHead>Giurato</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Invito</TableHead>
                            <TableHead>Voto</TableHead>
                            <TableHead>Startup Votata</TableHead>
                            <TableHead className="text-right">Azioni</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {results.judges_report.map(j => (
                            <TableRow key={j.judge_id} className="border-white/5" data-testid={`judge-report-${j.judge_id}`}>
                              <TableCell className="font-medium">{j.name}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{j.email}</TableCell>
                              <TableCell>
                                {j.email_sent ? (
                                  <span className="text-xs text-green-400 flex items-center gap-1" data-testid={`email-sent-${j.judge_id}`}>
                                    <Mail size={12} />Inviata{j.email_sent_count > 1 ? ` (${j.email_sent_count})` : ''}
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">Non inviata</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {j.has_voted ? (
                                  <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400 flex items-center gap-1 w-fit"><CheckCircle size={12} />Votato</span>
                                ) : (
                                  <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400 flex items-center gap-1 w-fit"><Clock size={12} />In attesa</span>
                                )}
                              </TableCell>
                              <TableCell className="font-medium">{j.voted_startup || '-'}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {selectedChallenge.status !== 'draft' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => sendEmails([j.judge_id])}
                                      disabled={sending}
                                      title={j.email_sent ? 'Reinvia email' : 'Invia email'}
                                      data-testid={`resend-email-${j.judge_id}`}
                                    >
                                      <Send size={14} />
                                    </Button>
                                  )}
                                  {j.has_voted && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-destructive"
                                      onClick={() => deleteJudgeVote(j.judge_id)}
                                      title="Cancella voto"
                                      data-testid={`delete-vote-${j.judge_id}`}
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* ─── CONTENT (BLOCKS) TAB ─── */}
          <TabsContent value="content" className="space-y-4 mt-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-muted-foreground">Personalizza la pagina pubblica della challenge con blocchi di testo, immagini, CTA.</p>
              <Button size="sm" onClick={saveChallengeContent} disabled={savingContent} data-testid="save-challenge-content-btn">
                {savingContent ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
                Salva Contenuto
              </Button>
            </div>
            <BlocksTab
              formData={contentForm}
              setFormData={setContentForm}
              publicUrl={selectedChallenge?.slug ? `${window.location.origin}/challenges/${selectedChallenge.slug}` : null}
            />
          </TabsContent>
        </Tabs>

        {/* ─── JUDGE DIALOG ─── */}
        <Dialog open={judgeDialogOpen} onOpenChange={(o) => { setJudgeDialogOpen(o); if (!o) resetJudgeForm(); }}>
          <DialogContent className="max-w-lg bg-card border-white/10">
            <DialogHeader><DialogTitle>{editingJudge ? 'Modifica Giurato' : 'Aggiungi Giurato'}</DialogTitle></DialogHeader>
            {!editingJudge && (
              <div className="space-y-2 mb-4">
                <Label className="text-xs text-muted-foreground">Cerca giurato esistente (dalle altre challenge)</Label>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input data-testid="judge-search-input" value={speakerSearch} onChange={(e) => searchSpeakers(e.target.value)} placeholder="Cerca per nome o email..." className="bg-white/5 border-white/10 pl-9" />
                </div>
                {speakerResults.length > 0 && (
                  <div className="max-h-36 overflow-y-auto space-y-1 p-2 bg-white/5 rounded-lg border border-white/10" data-testid="judge-search-results">
                    {speakerResults.map(s => (
                      <button key={s.email} type="button" onClick={() => fillFromSpeaker(s)} className="w-full text-left p-2 rounded hover:bg-white/10 text-sm flex items-center gap-2">
                        {s.image ? <img src={s.image.startsWith('http') ? s.image : `${process.env.REACT_APP_BACKEND_URL}${s.image}`} alt="" className="w-7 h-7 rounded-full object-cover" /> : <div className="w-7 h-7 rounded-full bg-primary/20" />}
                        <span className="truncate">{s.first_name} {s.last_name}{s.job_title ? ` · ${s.job_title}` : ''} <span className="text-muted-foreground text-xs">({s.email})</span></span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <form onSubmit={handleJudgeSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Nome *</Label><Input value={judgeForm.first_name} onChange={e => setJudgeForm({ ...judgeForm, first_name: e.target.value })} required className="bg-white/5 border-white/10" /></div>
                <div className="space-y-1"><Label>Cognome *</Label><Input value={judgeForm.last_name} onChange={e => setJudgeForm({ ...judgeForm, last_name: e.target.value })} required className="bg-white/5 border-white/10" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Job Title</Label><Input value={judgeForm.job_title} onChange={e => setJudgeForm({ ...judgeForm, job_title: e.target.value })} className="bg-white/5 border-white/10" /></div>
                <div className="space-y-1"><Label>Email *</Label><Input type="email" value={judgeForm.email} onChange={e => setJudgeForm({ ...judgeForm, email: e.target.value })} required className="bg-white/5 border-white/10" /></div>
              </div>
              <ImageUpload value={judgeForm.image} onChange={url => setJudgeForm({ ...judgeForm, image: url })} label="Foto" />
              <div className="space-y-1"><Label>Bio</Label><Textarea value={judgeForm.bio} onChange={e => setJudgeForm({ ...judgeForm, bio: e.target.value })} className="bg-white/5 border-white/10 min-h-[60px]" /></div>
              <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
                <Button type="button" variant="outline" onClick={() => setJudgeDialogOpen(false)}>Annulla</Button>
                <Button type="submit">{editingJudge ? 'Aggiorna' : 'Aggiungi'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* ─── STARTUP DIALOG ─── */}
        <Dialog open={startupDialogOpen} onOpenChange={(o) => { setStartupDialogOpen(o); if (!o) resetStartupForm(); }}>
          <DialogContent className="max-w-lg bg-card border-white/10">
            <DialogHeader><DialogTitle>{editingStartup ? 'Modifica Startup' : 'Aggiungi Startup'}</DialogTitle></DialogHeader>
            {!editingStartup && (
              <div className="space-y-2 mb-4">
                <Label className="text-xs text-muted-foreground">Cerca startup esistente (dalle altre challenge)</Label>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input data-testid="startup-search-input" value={startupSearch} onChange={(e) => searchStartups(e.target.value)} placeholder="Cerca per nome..." className="bg-white/5 border-white/10 pl-9" />
                </div>
                {startupSearchResults.length > 0 && (
                  <div className="max-h-36 overflow-y-auto space-y-1 p-2 bg-white/5 rounded-lg border border-white/10" data-testid="startup-search-results">
                    {startupSearchResults.map(s => (
                      <button key={s.name} type="button" onClick={() => fillFromStartup(s)} className="w-full text-left p-2 rounded hover:bg-white/10 text-sm flex items-center gap-2">
                        {s.logo ? <img src={s.logo.startsWith('http') ? s.logo : `${process.env.REACT_APP_BACKEND_URL}${s.logo}`} alt="" className="w-7 h-7 rounded object-contain bg-white/10 p-0.5" /> : <div className="w-7 h-7 rounded bg-primary/20" />}
                        <span className="truncate">{s.name}{s.description ? ` · ${s.description.slice(0, 60)}` : ''}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <form onSubmit={handleStartupSubmit} className="space-y-3">
              <div className="space-y-1"><Label>Nome Startup *</Label><Input value={startupForm.name} onChange={e => setStartupForm({ ...startupForm, name: e.target.value })} required className="bg-white/5 border-white/10" /></div>
              <ImageUpload value={startupForm.logo} onChange={url => setStartupForm({ ...startupForm, logo: url })} label="Logo" />
              <div className="space-y-1"><Label>Descrizione</Label><Textarea value={startupForm.description} onChange={e => setStartupForm({ ...startupForm, description: e.target.value })} className="bg-white/5 border-white/10 min-h-[60px]" /></div>
              <div className="space-y-1"><Label>Sito web</Label><Input type="url" placeholder="https://..." value={startupForm.website} onChange={e => setStartupForm({ ...startupForm, website: e.target.value })} className="bg-white/5 border-white/10" /></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1"><Label>LinkedIn</Label><Input type="url" placeholder="https://linkedin.com/company/..." value={startupForm.linkedin} onChange={e => setStartupForm({ ...startupForm, linkedin: e.target.value })} className="bg-white/5 border-white/10" /></div>
                <div className="space-y-1"><Label>Instagram</Label><Input type="url" placeholder="https://instagram.com/..." value={startupForm.instagram} onChange={e => setStartupForm({ ...startupForm, instagram: e.target.value })} className="bg-white/5 border-white/10" /></div>
              </div>
              <ImageUpload value={startupForm.founder_photo} onChange={url => setStartupForm({ ...startupForm, founder_photo: url })} label="Foto fondatore" />
              <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
                <Button type="button" variant="outline" onClick={() => setStartupDialogOpen(false)}>Annulla</Button>
                <Button type="submit">{editingStartup ? 'Aggiorna' : 'Aggiungi'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* ─── IMPORT STARTUPS EXCEL DIALOG ─── */}
        <ImportStartupsDialog
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          challengeId={selectedChallenge?.id}
          onImported={() => selectedChallenge && loadChallengeDetail(selectedChallenge)}
        />

        {/* ─── SPAWN CHILD DIALOG ─── */}
        <SpawnChildDialog
          open={spawnDialogOpen}
          onOpenChange={(o) => { setSpawnDialogOpen(o); if (!o) { setSpawnTitle(''); setSpawnStartupIds([]); } }}
          title={spawnTitle}
          setTitle={setSpawnTitle}
          ranking={results?.ranking || []}
          judgesCount={judges.length}
          startupIds={spawnStartupIds}
          toggleStartup={toggleSpawnStartup}
          onConfirm={handleSpawn}
          spawning={spawning}
        />


        {/* Challenge Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogContent className="max-w-3xl bg-card border-white/10 max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Modifica Challenge</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <Tabs defaultValue="info">
                <TabsList>
                  <TabsTrigger value="info">Info generali</TabsTrigger>
                  <TabsTrigger value="content">Contenuto pagina</TabsTrigger>
                </TabsList>
                <TabsContent value="info" className="space-y-3 mt-4">
                  <div className="space-y-1"><Label>Titolo *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className="bg-white/5 border-white/10" /></div>
                  <div className="space-y-1">
                    <Label>Nome nel menu (mostrato nella pagina dell'evento)</Label>
                    <Input value={form.nav_label} onChange={e => setForm({ ...form, nav_label: e.target.value })} placeholder="Es. Giuria" className="bg-white/5 border-white/10" data-testid="challenge-nav-label-input" />
                  </div>
                  <div className="space-y-1">
                    <Label>Evento</Label>
                    <Select value={form.event_id} onValueChange={v => setForm({ ...form, event_id: v })}>
                      <SelectTrigger className="bg-white/5 border-white/10"><SelectValue placeholder="Seleziona evento..." /></SelectTrigger>
                      <SelectContent>{events.map(ev => <SelectItem key={ev.id} value={ev.id}>{ev.title}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label>Inizio *</Label><Input type="datetime-local" value={form.start_time?.slice(0,16)} onChange={e => setForm({ ...form, start_time: e.target.value })} required className="bg-white/5 border-white/10" /></div>
                    <div className="space-y-1"><Label>Fine *</Label><Input type="datetime-local" value={form.end_time?.slice(0,16)} onChange={e => setForm({ ...form, end_time: e.target.value })} required className="bg-white/5 border-white/10" /></div>
                  </div>
                  <div className="space-y-1"><Label>Descrizione breve</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-white/5 border-white/10 min-h-[60px]" /></div>
                  <div className="space-y-1">
                    <Label>Stato</Label>
                    <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                      <SelectTrigger className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Bozza</SelectItem>
                        <SelectItem value="active">Attiva</SelectItem>
                        <SelectItem value="closed">Chiusa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>
                <TabsContent value="content" className="mt-4">
                  <BlocksTab
                    formData={form}
                    setFormData={setForm}
                    publicUrl={form.slug ? `${window.location.origin}/challenges/${form.slug}` : null}
                  />
                </TabsContent>
              </Tabs>
              <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                <Button type="submit">Salva</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ═══════════ CHALLENGE LIST ═══════════
  return (
    <div className="space-y-8" data-testid="challenges-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Startup Challenge</h1>
          <p className="text-muted-foreground mt-1">Gestisci le challenge e le votazioni</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="create-challenge-btn" onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus size={18} className="mr-2" />Nuova Challenge
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-card border-white/10">
            <DialogHeader><DialogTitle>Nuova Challenge</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-1"><Label>Titolo *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className="bg-white/5 border-white/10" data-testid="challenge-title-input" /></div>
              <div className="space-y-1">
                <Label>Nome nel menu (mostrato nella pagina dell'evento)</Label>
                <Input value={form.nav_label} onChange={e => setForm({ ...form, nav_label: e.target.value })} placeholder="Es. Giuria" className="bg-white/5 border-white/10" />
              </div>
              <div className="space-y-1">
                <Label>Evento</Label>
                <Select value={form.event_id} onValueChange={v => setForm({ ...form, event_id: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10"><SelectValue placeholder="Seleziona evento..." /></SelectTrigger>
                  <SelectContent>{events.map(ev => <SelectItem key={ev.id} value={ev.id}>{ev.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Inizio *</Label><Input type="datetime-local" value={form.start_time?.slice(0,16)} onChange={e => setForm({ ...form, start_time: e.target.value })} required className="bg-white/5 border-white/10" /></div>
                <div className="space-y-1"><Label>Fine *</Label><Input type="datetime-local" value={form.end_time?.slice(0,16)} onChange={e => setForm({ ...form, end_time: e.target.value })} required className="bg-white/5 border-white/10" /></div>
              </div>
              <div className="space-y-1"><Label>Descrizione breve</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-white/5 border-white/10 min-h-[60px]" /></div>
              <p className="text-xs text-muted-foreground">Per aggiungere blocchi di contenuto (testo + immagini), salva la challenge e clicca su "Modifica".</p>
              <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                <Button type="submit" data-testid="save-challenge-btn">Crea Challenge</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="glass rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead>Challenge</TableHead>
              <TableHead>Evento</TableHead>
              <TableHead>Periodo</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {challenges.map(ch => (
              <TableRow key={ch.id} className="border-white/5 cursor-pointer hover:bg-white/5" onClick={() => loadChallengeDetail(ch)} data-testid={`challenge-row-${ch.id}`}>
                <TableCell className="font-medium">{ch.title}</TableCell>
                <TableCell>{getEventTitle(ch.event_id)}</TableCell>
                <TableCell className="text-sm">
                  <div className="flex items-center gap-1 text-muted-foreground"><Clock size={12} />{ch.start_time?.slice(0,16).replace('T',' ')}</div>
                  <div className="text-xs text-muted-foreground">{ch.end_time?.slice(0,16).replace('T',' ')}</div>
                </TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-1 rounded-full ${ch.status === 'active' ? 'bg-green-500/20 text-green-400' : ch.status === 'closed' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {ch.status === 'active' ? 'Attiva' : ch.status === 'closed' ? 'Chiusa' : 'Bozza'}
                  </span>
                </TableCell>
                <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(ch)}><Pencil size={16} /></Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(ch.id)}><Trash2 size={16} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {challenges.length === 0 && !loading && (
              <TableRow><TableCell colSpan={5} className="text-center py-12"><Trophy className="mx-auto text-muted-foreground mb-4" size={32} /><p className="text-muted-foreground">Nessuna challenge creata</p></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
